import React, { useContext, useState } from "react";
import { AuthContext } from "@/auth/AuthContext";
import { type UserType } from "@/utils/types";
import { useQuery } from "@tanstack/react-query";
import { getCompanies } from "@/auth/operations";
import { updateUsersCompany } from "@/utils/firebase_operations";
import { useAtomValue } from "jotai";
import getBuildsAtom from "../atoms/get-builds-atom";
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Help, KeyboardArrowDown, Logout, MoreVert, SwapHoriz } from "@mui/icons-material";
import styled from "@emotion/styled";
import { getPylonVerificationHash } from "@/firebase";

const ITEM_HEIGHT = 48;

const CompaniesChangeDropdown = ({
  companies,
  user,
}: {
  companies: any[];
  user: UserType;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(4px)",
          flexDirection: "column",
          gap: 3,
        }}
        open={switchingTo !== null}
      >
        <SwapHoriz
          sx={{
            fontSize: 48,
            color: "primary.main",
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 0.4, transform: "scale(0.95)" },
              "50%": { opacity: 1, transform: "scale(1.1)" },
            },
          }}
        />
        <CircularProgress size={32} thickness={4} />
        <Typography variant="h6" color="text.secondary" fontWeight={500}>
          Switching to {switchingTo}...
        </Typography>
      </Backdrop>
      {companies && (
        <>
          <IconButton
            aria-label="more"
            id="long-button"
            aria-controls={open ? "long-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreVert />
          </IconButton>
          <Menu
            id="long-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              paper: {
                style: {
                  maxHeight: ITEM_HEIGHT * 6,
                  width: "20ch",
                },
              },
              list: {
                "aria-labelledby": "long-button",
              },
            }}
          >
            {companies.map((company) => (
              <MenuItem
                key={company.id}
                value={company.id}
                id={company.id}
                disabled={company.id === user.companyId || switchingTo !== null}
                onClick={async () => {
                  setSwitchingTo(company.name);
                  handleClose();
                  await updateUsersCompany(company.id);
                  location.reload();
                }}
              >
                {company.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </>
  );
};

const LogoutButton = ({
  user,
  logoutHandler,
}: {
  user: UserType;
  logoutHandler: () => any;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        sx={{
          maxWidth: "220px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.email}
        </span>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem disabled>{user.companyName}</MenuItem>
        <MenuItem
          onClick={() => {
            logoutHandler();
            handleClose();
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

const CompanyList = ({ user }: { user: UserType }) => {
  const { isLoading, data, isError, error } = useQuery({
    queryKey: ["companyNames"],
    queryFn: async () => await getCompanies(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return (
    <>{data && <CompaniesChangeDropdown companies={data} user={user} />}</>
  );
};

const TestingAppBar = ({
  headerLabel,
  className,
}: {
  headerLabel?: string;
  className?: string;
}) => {
  const useAuth = () => useContext<any>(AuthContext);
  const { user, logOut } = useAuth();

  const lastBuildQuery = useAtomValue(
    getBuildsAtom({ organizationId: user.companyId, limit: 20 })
  );

  return (
    <>
      {!user.isAnonymous && (
        <StyledHeader className={className}>
          {import.meta.env.MODE !== "production" && (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Alert
                style={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                severity={import.meta.env.MODE === "beta" ? "warning" : "info"}
              >
                {import.meta.env.MODE === "beta"
                  ? "BETA FRONTEND"
                  : "DEVELOPMENT FRONTEND"}
              </Alert>
            </div>
          )}
          <PylonButton />
          {!lastBuildQuery.isLoading &&
            lastBuildQuery.data &&
            lastBuildQuery.data[0]?.iconUrl && (
              <img
                src={lastBuildQuery.data[0]?.iconUrl}
                className="h-8 rounded-2xl"
                alt={"logo"}
              />
            )}
          <LogoutButton user={user} logoutHandler={logOut} />
          {user.role === "superUser" && <CompanyList user={user} />}
        </StyledHeader>
      )}
    </>
  );
};

const PylonButton = () => {
  const { data } = useQuery({
    queryKey: ["pylon"],
    queryFn: async () => {
      const response = await getPylonVerificationHash();

      if (response.data.hash) {
        window.pylon.chat_settings.email_hash = response.data.hash;
      }

      return response.data.hash;
    },
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return (
    <>
      {data && (
        <Button
          onClick={() => {
            window.Pylon("show");
          }}
          variant="outlined"
          startIcon={<Help />}
          sx={{ flexShrink: 0 }}
        >
          Help
        </Button>
      )}
    </>
  );
};

const StyledHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  alignItems: "center",
  minWidth: 0,
  backgroundColor: "#ffffff",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `1px solid ${theme.palette.divider}`,
  height: "61px",
  gap: "24px",
  paddingTop: "8px",
  paddingBottom: "8px",
  paddingLeft: "16px",
  paddingRight: "16px",
  overflow: "hidden",

  [theme.breakpoints.up("lg")]: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "32px",
  },
}));

export default TestingAppBar;
